import { FC, useEffect, useState } from "react";
import {
  Flex,
  FlexProps,
  FormControl,
  FormErrorMessage,
  FormLabel,
  useRadioGroup,
} from "@chakra-ui/react";
import { useField, useFormikContext } from "formik";
import { RadioCard } from "./RadioCard";

export interface RadioOption {
  label: string;
  value: string;
}

interface BaseProps {
  autoAdvance?: boolean | ((option: string) => boolean);
  defaultValue?: string;
  id: string;
  label?: string;
  radioGroupProps?: FlexProps;
}

/**
 * Exactly one source of radio options is required. Provide `options` when the
 * displayed label differs from the stored value (e.g. enum-backed answers);
 * provide `values` as a shorthand where label === value.
 */
type Props =
  | (BaseProps & { options: RadioOption[]; values?: never })
  | (BaseProps & { values: string[]; options?: never });

export const RadioInput: FC<Props> = ({
  autoAdvance,
  defaultValue,
  id,
  label,
  options,
  radioGroupProps,
  values,
}) => {
  const radioOptions: RadioOption[] =
    options ?? (values ?? []).map((value) => ({ label: value, value }));
  const [field, meta] = useField(id);
  const formik = useFormikContext();
  const { getRootProps, getRadioProps } = useRadioGroup({
    name: id,
    defaultValue: defaultValue ?? "",
  });
  const [interacted, setInteracted] = useState(false);
  const [shouldAdvance, setShouldAdvance] = useState(false);

  const { submitForm } = formik;
  const { value } = field;

  useEffect(() => {
    if (!interacted) return;

    if (typeof autoAdvance === "function") {
      autoAdvance(value) && setShouldAdvance(true);
    } else if (autoAdvance) {
      setShouldAdvance(true);
    }
  }, [autoAdvance, value, interacted, submitForm]);

  useEffect(() => {
    if (shouldAdvance) {
      submitForm();
    }
  }, [shouldAdvance, submitForm]);

  const handleChange = (e: React.ChangeEvent<any>) => {
    field.onChange(e);
    setInteracted(true);
  };
  const group = getRootProps();

  return (
    <FormControl isInvalid={meta.touched && !!meta.error}>
      {label && <FormLabel htmlFor={id}>{label}</FormLabel>}

      <Flex
        direction="column"
        id={id}
        w="full"
        {...radioGroupProps}
        {...field}
        {...group}
        onChange={handleChange}
        gap={2}
      >
        {radioOptions.map(({ label: optionLabel, value }) => {
          const radio = getRadioProps({ value });
          return <RadioCard key={value} label={optionLabel} {...radio} />;
        })}
      </Flex>

      <FormErrorMessage>{meta.error}</FormErrorMessage>
    </FormControl>
  );
};
